/**
 * MUI theme options — the dashboard app only. The POS uses Tailwind and must not load this.
 *
 * `components` styleOverrides use `var(--pos-…)`, the same variables the Tailwind preset emits —
 * those are plain CSS strings MUI applies verbatim, so a theme switch is a class on `<html>`, not
 * a rebuild. `palette` cannot use them: MUI computes hover/focus/disabled overlays by calling its
 * own `alpha()`/`darken()` on the palette colours at theme-build time, and those parse `#rrggbb`,
 * not `var(...)`. So palette entries take the resolved hex for the mode being built, from the same
 * `themes` object the CSS variables are generated from — one source, two representations.
 */

import type { ThemeOptions } from '@mui/material/styles';

import { density, radius, breakpoint, zIndex } from '../primitives/layout.js';
import { fontWeight, numericFeatures, type typeScale } from '../primitives/typography.js';
import { v } from '../css/variables.js';
import { themes } from '../semantic/themes.js';
import { shopThemeVariables, themeLookFor } from '../semantic/shopThemes.js';

const typeVariant = (name: keyof typeof typeScale) => ({
  fontSize: v('text', name, 'size'),
  lineHeight: v('text', name, 'leading'),
  fontWeight: `var(--pos-text-${name}-weight)`,
  letterSpacing: v('text', name, 'tracking'),
});

/**
 * `mode` is passed in rather than read, because the dashboard sets `data-theme` on `<html>` and
 * MUI needs to be told which one it is for the handful of internal decisions it makes from
 * `palette.mode` (ripple colour, backdrop alpha).
 */
export function muiThemeOptions(mode: 'light' | 'dark', shopThemeCode?: string): ThemeOptions {
  const base = themes[mode];

  /*
   * The shop's look, resolved to hex for the palette. The CSS side follows `data-shop-theme`
   * on its own; this is the half MUI cannot do from a variable, so it has to be rebuilt when the
   * shop changes — the dashboard's provider keys on it. Without this, every button, link, tab
   * and chip stays the product's moss on a shop whose page is Diamond's near-black.
   *
   * Only the roles a shop theme may repaint are taken from it. Status colours, `action`, focus
   * and the rest come from the base theme regardless — the same rule the stylesheet applies.
   */
  // `themeLookFor`, not `shopThemeFor`: a signed-out page carries `neutral`, which is not a shop
  // and which the shop-only lookup would resolve to the default — putting moss back on a page
  // whose stylesheet had gone grey.
  const shop = shopThemeCode === undefined ? undefined : shopThemeVariables(themeLookFor(shopThemeCode), mode);
  const pick = (key: string, fallback: string) => shop?.[key] ?? fallback;

  const colors = {
    ...base,
    surface: {
      ...base.surface,
      base: pick('--pos-surface-base', base.surface.base),
      raised: pick('--pos-surface-raised', base.surface.raised),
    },
    text: {
      ...base.text,
      primary: pick('--pos-text-primary', base.text.primary),
      secondary: pick('--pos-text-secondary', base.text.secondary),
      disabled: pick('--pos-text-disabled', base.text.disabled),
    },
    border: {
      ...base.border,
      subtle: pick('--pos-border-subtle', base.border.subtle),
    },
    brand: {
      base: pick('--pos-brand-base', base.brand.base),
      hover: pick('--pos-brand-hover', base.brand.hover),
      active: pick('--pos-brand-active', base.brand.active),
      subtle: pick('--pos-brand-subtle', base.brand.subtle),
      subtleText: pick('--pos-brand-subtle-text', base.brand.subtleText),
      on: pick('--pos-brand-on', base.brand.on),
    },
    // A full-surface theme repaints the button colour too; the brand-only ones leave it copper.
    action: {
      base: pick('--pos-action-base', base.action.base),
      hover: pick('--pos-action-hover', base.action.hover),
      active: pick('--pos-action-active', base.action.active),
      subtle: pick('--pos-action-subtle', base.action.subtle),
      subtleText: pick('--pos-action-subtle-text', base.action.subtleText),
      on: pick('--pos-action-on', base.action.on),
    },
  };

  return {
    // MUI's own `cssVariables` mode is deliberately not enabled. It would generate a second set
    // of variables from the palette, and the palette here is already variables — two indirections
    // over one value, with MUI's names winning in some components and ours in others.
    palette: {
      mode,
      // Moss is `primary`. MUI wires `primary` into every default button, switch, focused input
      // and active pagination page, and the OriTech design contract is explicit that a primary
      // action is green — "+ New Sale", "Save Product", "Sign in" are all the sage-green button.
      // (This was copper until the design system was written down; copper is the accent, and an
      // accent that lands on every button in the product stops being an accent.)
      primary: {
        main: colors.brand.base,
        light: colors.brand.subtle,
        dark: colors.brand.active,
        contrastText: colors.brand.on,
      },
      // Copper is `secondary`: the accent. Restrained by design — it carries emphasis where green
      // would be ambiguous, and it stays the focus-ring colour.
      secondary: {
        main: colors.action.base,
        light: colors.action.subtle,
        dark: colors.action.active,
        contrastText: colors.action.on,
      },
      success: { main: colors.success.solid, light: colors.success.subtle, dark: colors.success.solidHover, contrastText: colors.success.on },
      warning: { main: colors.warning.solid, light: colors.warning.subtle, dark: colors.warning.solidHover, contrastText: colors.warning.on },
      error: { main: colors.danger.solid, light: colors.danger.subtle, dark: colors.danger.solidHover, contrastText: colors.danger.on },
      info: { main: colors.info.solid, light: colors.info.subtle, dark: colors.info.solidHover, contrastText: colors.info.on },
      background: { default: colors.surface.base, paper: colors.surface.raised },
      text: { primary: colors.text.primary, secondary: colors.text.secondary, disabled: colors.text.disabled },
      divider: colors.border.subtle,
    },

    shape: {
      // MUI takes a number. The dashboard is denser than the POS, so it uses the `md` step;
      // anything wanting the generous `lg` asks for it by token at the component.
      borderRadius: 14,
    },

    breakpoints: {
      values: {
        xs: 0,
        sm: Number.parseInt(breakpoint.sm, 10),
        md: Number.parseInt(breakpoint.md, 10),
        lg: Number.parseInt(breakpoint.lg, 10),
        xl: Number.parseInt(breakpoint.xl, 10),
      },
    },

    zIndex: {
      appBar: zIndex.header,
      drawer: zIndex.drawer,
      modal: zIndex.dialog,
      snackbar: zIndex.toast,
      tooltip: zIndex.tooltip,
    },

    typography: {
      fontFamily: v('font', 'sans'),
      fontWeightRegular: fontWeight.regular,
      fontWeightMedium: fontWeight.medium,
      fontWeightBold: fontWeight.bold,
      h1: typeVariant('4xl'),
      h2: typeVariant('3xl'),
      h3: typeVariant('2xl'),
      h4: typeVariant('xl'),
      h5: typeVariant('lg'),
      h6: typeVariant('lg'),
      body1: typeVariant('base'),
      body2: typeVariant('sm'),
      caption: typeVariant('xs'),
      overline: typeVariant('2xs'),
      button: { ...typeVariant('sm'), textTransform: 'none' },
    },

    components: {
      // MUI's default shadows are neutral grey. Replaced with the warm set, or every dashboard
      // card would sit under a different light than every POS card.
      // Collapsed onto the three semantic levels. MUI hands components a numeric elevation from a
      // 25-step scale; mapping the ones it actually uses onto flat/raised/overlay is what stops
      // that scale leaking back in as five subtly different lifts nobody chose.
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: radius.card },
          elevation1: { boxShadow: v('elevation', 'flat') },
          elevation2: { boxShadow: v('elevation', 'raised') },
          elevation3: { boxShadow: v('elevation', 'raised') },
          // Menus and poppers default to 8; dialogs to 24.
          elevation8: { boxShadow: v('elevation', 'raised') },
          elevation24: { boxShadow: v('elevation', 'overlay') },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            transition: v('transition', 'control'),
            '&:active': { transform: 'scale(var(--pos-press-scale))' },
          },
          // 48px, so a `large` button lines up with a `medium` input beside or beneath it. MUI's
          // own large is 42.75px, which reads as almost-but-not-quite against a 48px field.
          sizeLarge: { minHeight: 48 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: radius.md },
          // The default 1px divider-coloured outline does not reach 3:1. `border.strong` does.
          notchedOutline: { borderColor: v('border', 'strong') },
          // 48px total rather than MUI's 56px. This is a dense operational product — a 56px field
          // is a consumer sign-up form, and a form of eight of them becomes a scroll.
          input: { paddingTop: '12.5px', paddingBottom: '12.5px' },
          // Multiline puts its padding on the root and zeroes it here; without this the override
          // above would double up inside every note/reason textarea.
          inputMultiline: { paddingTop: 0, paddingBottom: 0 },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          // The resting (un-shrunk) label is centred against the input's own height, so it has to
          // move with the padding above — 12.5px, matching. `small` is restored to MUI's own 9px
          // because small inputs keep MUI's 8.5px padding: the dense filter rows are already right.
          outlined: {
            transform: 'translate(14px, 12.5px) scale(1)',
            '&.MuiInputLabel-sizeSmall': { transform: 'translate(14px, 9px) scale(1)' },
            '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: radius.sheet } },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: v('scrim') } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: v('surface', 'inverse'),
            color: v('text', 'inverse'),
            borderRadius: radius.sm,
          },
        },
      },
      // Every card in both apps used to set its own border/radius by hand — one table's card
      // and another feature's card drifted a pixel apart with nobody deciding that on purpose.
      // `outlined` is the house default: a subtle border reads as "part of the page" in a dense
      // operational app, where MUI's default drop shadow on every card reads as "floating widget."
      // A card is defined by its border, never by lift — see `elevation.ts`. `border.card` rather
      // than `border.subtle`: white-on-cream separates by 1.13:1, which is not an edge, and is
      // exactly why a screen of twelve panels read as floating rather than as surfaces.
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            borderColor: v('border', 'card'),
            backgroundColor: v('surface', 'raised'),
            boxShadow: v('elevation', 'flat'),
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          // MUI's default is 16px with a 24px last-child bottom, which is where the wasted
          // vertical space came from. One padding, stated once.
          root: {
            padding: density.cardPadding,
            '&:last-child': { paddingBottom: density.cardPadding },
          },
        },
      },
      // A status chip communicates state at a glance across sales, payments, purchases and
      // shifts — it has to look identical everywhere or "completed" stops meaning one thing.
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.chip, fontWeight: fontWeight.medium },
          sizeSmall: { height: 22, fontSize: v('text', 'xs', 'size') },
        },
      },

      // Plain `Table` is still the right tool for a short, non-paginated list (roles, shops, the
      // prices on one variant). These overrides give those the same header shading and row rhythm
      // the DataGrid gets from its own theme entry, so the two never look like different products.
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: v('border', 'subtle'),
            paddingTop: '10px',
            paddingBottom: '10px',
            // Every table in this product has a money or quantity column. Tabular figures were
            // defined in the type tokens from the start but never actually applied here, so
            // columns of prices did not line up. Harmless on the text columns beside them.
            fontVariantNumeric: numericFeatures.money,
          },
          head: {
            backgroundColor: v('surface', 'sunken'),
            color: v('text', 'secondary'),
            fontSize: v('text', 'xs', 'size'),
            fontWeight: fontWeight.bold,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          },
          sizeSmall: { paddingTop: '8px', paddingBottom: '8px' },
        },
      },

      MuiTablePagination: {
        defaultProps: {
          // "Showing 1–10 of 124" rather than MUI's bare "1–10 of 124". Set once, so the wording
          // is identical under every table and grid in the product.
          labelDisplayedRows: ({ from, to, count }) =>
            `Showing ${from}–${to} of ${count === -1 ? 'many' : count}`,
          labelRowsPerPage: 'Per page',
        },
        styleOverrides: {
          root: { borderTop: 'none' },
          selectLabel: { fontSize: v('text', 'xs', 'size'), color: v('text', 'secondary') },
          displayedRows: { fontSize: v('text', 'xs', 'size'), color: v('text', 'secondary') },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42, borderBottom: `1px solid ${v('border', 'subtle')}` },
          indicator: { height: 2, borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 42,
            padding: '0 12px',
            fontWeight: fontWeight.medium,
            color: v('text', 'secondary'),
            '&.Mui-selected': { fontWeight: fontWeight.bold },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: radius.md, border: `1px solid ${v('border', 'subtle')}` },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: v('text', 'sm', 'size'), minHeight: 38 } },
      },

      MuiDrawer: {
        styleOverrides: { paper: { backgroundImage: 'none' } },
      },

      MuiAlert: {
        styleOverrides: { root: { borderRadius: radius.md } },
      },

      MuiSkeleton: {
        styleOverrides: { root: { borderRadius: radius.xs, backgroundColor: v('surface', 'sunken') } },
      },

      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: radius.sm } },
      },
    },
  };
}

/**
 * The pattern every `DataGrid` in the dashboard duplicated by hand: a subtle border, the card
 * radius, a raised surface, and a shaded header row so the header reads as structure rather than
 * another data row. Individual tables still set height/density themselves — that varies by
 * screen — but never this.
 * <para>
 * Exported separately, not folded into `muiThemeOptions`'s `components`: `@mui/x-data-grid`'s
 * theme augmentation (the module that makes `Components['MuiDataGrid']` a known key) is not a
 * dependency of this package, only of the dashboard app that actually renders a `DataGrid`. The
 * dashboard's own `theme.ts` imports the augmentation and merges this in.
 */
export const dataGridStyleOverrides = {
  root: {
    border: `1px solid ${v('border', 'card')}`,
    borderRadius: radius.card,
    backgroundColor: v('surface', 'raised'),
    // Subtle separators rather than a grid of boxes. A business table wants rows the eye can
    // follow, not a spreadsheet's cell borders.
    '--DataGrid-rowBorderColor': v('border', 'subtle'),
  },
  columnHeaders: {
    backgroundColor: v('surface', 'sunken'),
    borderBottom: `1px solid ${v('border', 'subtle')}`,
  },
  columnHeaderTitle: {
    fontSize: v('text', 'xs', 'size'),
    fontWeight: fontWeight.bold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: v('text', 'secondary'),
  },
  // The drag handle between headers is noise on a table nobody resizes by hand.
  columnSeparator: { display: 'none' },
  cell: {
    borderColor: v('border', 'subtle'),
    fontSize: v('text', 'sm', 'size'),
    fontVariantNumeric: numericFeatures.money,
  },
  row: {
    '&:hover': { backgroundColor: v('surface', 'sunken') },
  },
  footerContainer: {
    minHeight: 52,
    borderTop: `1px solid ${v('border', 'subtle')}`,
  },
  toolbarContainer: {
    gap: 8,
    padding: '8px 8px 4px',
  },
  overlay: { backgroundColor: 'transparent' },
};

export default muiThemeOptions;
